import type { EmailAttachment } from '@/components/shared/single-email-modal';
import { isMissingDocumentError } from '@/lib/document-download-errors';

/**
 * Outcome of an attachment download batch.
 *
 * `foundCount` counts documents that exist and were attempted (documents the
 * source reported as unavailable are excluded), `failedCount` how many of those
 * failed to download, `missingCount` how many sources reported no document at
 * all. This lets callers tell "there are genuinely no documents"
 * (`foundCount === 0`) apart from "documents exist but could not be downloaded"
 * (`foundCount > 0` with no attachments). `errorMessage` carries a specific
 * reason when attachments could not even be attempted.
 */
export interface FetchAttachmentsResult {
  attachments: EmailAttachment[];
  foundCount: number;
  failedCount: number;
  missingCount?: number;
  errorMessage?: string;
  /** Transactions whose invoice document was actually downloaded and attached. */
  attachedTransactionIds?: number[];
}

/** No documents exist for the source — not an error. */
export function emptyAttachmentsResult(): FetchAttachmentsResult {
  return { attachments: [], foundCount: 0, failedCount: 0 };
}

const withoutAttachmentsSuffix = 'The email will be sent without attachments.';

/** Reasons attachments could not be attempted at all, shown to the user verbatim. */
export const attachmentErrorMessages = {
  noAuthentication: `Authentication is not available, so documents could not be fetched. ${withoutAttachmentsSuffix}`,
  noDocumentService: `Document management is not available for this company. ${withoutAttachmentsSuffix}`,
  documentLookupFailed: (transactionCount: number) =>
    `${transactionCount} transaction(s) could not be queried for documents. ${withoutAttachmentsSuffix}`,
} as const;

export function attachmentsError(errorMessage: string): FetchAttachmentsResult {
  return { attachments: [], foundCount: 0, failedCount: 0, errorMessage };
}

export async function fetchAttachmentsWithProgress<TId>(
  ids: TId[],
  downloadFn: (id: TId) => Promise<EmailAttachment>,
  onProgress: (done: number, total: number) => void,
): Promise<FetchAttachmentsResult> {
  if (ids.length === 0) return emptyAttachmentsResult();

  onProgress(0, ids.length);

  const results = await Promise.allSettled(
    ids.map(async (id, idx) => {
      const doc = await downloadFn(id);
      onProgress(idx + 1, ids.length);
      return doc;
    }),
  );

  const attachments = results
    .filter((r): r is PromiseFulfilledResult<EmailAttachment> => r.status === 'fulfilled')
    .map((r) => r.value);

  const missingCount = results.filter((r) => r.status === 'rejected' && isMissingDocumentError(r.reason)).length;

  const attachedTransactionIds = attachments.map((a) => a.transactionId).filter((id): id is number => id !== undefined);

  return {
    attachments,
    foundCount: ids.length - missingCount,
    failedCount: results.length - attachments.length - missingCount,
    missingCount,
    attachedTransactionIds,
  };
}
